import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LockPin } from 'assets/icons/LockPin.svg';
import {
  isLoginSecretStorageUnlockActive,
  submitLoginSecretStorageUnlock,
  useLoginSecretStorageUnlock,
} from 'contexts/MatrixContext/loginSecretStorageUnlock';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useMatrixDeviceVerificationContext from 'contexts/MatrixDeviceVerificationContext/useMatrixDeviceVerificationContext';
import ChooseVerificationMethodModal from 'modules/EndToEndEncryption/ChooseVerificationMethodModal/ChooseVerificationMethodModal';
import useSecretStorageSecurityCode from 'modules/EndToEndEncryption/hooks/useSecretStorageSecurityCode';
import logger from 'utils/logging/faro';
import testingConstants from 'utils/testingConstants';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

import SecurityCodeModalBase from '../CreateSecurityCodeModal/SecurityCodeModalBase';
import { SECURITY_CODE_LENGTH } from '../securityCodeConstants';
import SecurityCodeInput, { SecurityCodeInputHandle } from '../SecurityCodeInput/SecurityCodeInput';

interface UnlockWithSecurityCodeModalProps {
  onSuccess?: () => void;
  isLoginUnlock?: boolean;
  onLoginUnlockCancel?: () => void;
  onSubmitSecurityCode?: (securityCode: string) => Promise<void>;
}

interface UnlockWithSecurityCodeModalBodyProps extends UnlockWithSecurityCodeModalProps {
  isLoginUnlock: boolean;
  isSubmitting: boolean;
  activeErrorKey: string | null;
  clearActiveError: () => void;
  submitSecurityCode: (securityCode: string) => Promise<void>;
}

const UnlockWithSecurityCodeModalBody = ({
  onSuccess,
  isLoginUnlock,
  onLoginUnlockCancel,
  isSubmitting,
  activeErrorKey,
  clearActiveError,
  submitSecurityCode,
}: UnlockWithSecurityCodeModalBodyProps) => {
  const { t } = useTranslation();
  const modal = useModal(UnlockWithSecurityCodeModal);
  const chooseVerificationMethodModal = useModal(ChooseVerificationMethodModal);
  const { setHasChosenSasVerification, cancelVerification } = useMatrixDeviceVerificationContext();
  const { devicesVerificationStatus, markDeviceVerified, refreshHasSecurityCode } = useMatrixDeviceContext();

  const [securityCode, setSecurityCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const passcodeRef = useRef<SecurityCodeInputHandle>(null);

  useEffect(() => {
    if (activeErrorKey) setIsShaking(true);
  }, [activeErrorKey]);

  const displayError = localError || (activeErrorKey ? t(activeErrorKey) : null);

  const handleSubmit = async () => {
    try {
      Schemas.getEnterSecurityCodeValidation().validateSync({ securityCode });
    } catch (validationError) {
      if (validationError instanceof Error) {
        setLocalError(validationError.message);
        logger.warn('UnlockWithSecurityCodeModal: validation failed', validationError.message);
      }
      setSecurityCode('');
      passcodeRef.current?.focusFirst();
      return;
    }

    try {
      await submitSecurityCode(securityCode);

      if (!isLoginUnlock) {
        markDeviceVerified();
        await refreshHasSecurityCode();
      }

      onSuccess?.();
      modal.remove();
    } catch (err) {
      logger.warn('UnlockWithSecurityCodeModal: unlock failed', err instanceof Error ? err.message : String(err));
      setSecurityCode('');
      passcodeRef.current?.focusFirst();
    }
  };

  const handleChange = (newValue: string) => {
    setSecurityCode(newValue);
    if (localError) setLocalError(null);
    if (activeErrorKey) clearActiveError();
  };

  const handleChangeMethod = () => {
    cancelVerification(false);
    setHasChosenSasVerification(false);
    modal.remove();
    chooseVerificationMethodModal.show({
      isLoginUnlock,
      onLoginUnlockCancel,
      onVerificationComplete: () => {
        onSuccess?.();
      },
    });
  };

  // During login unlock the user must always be able to return to the method picker — they may
  // want to reset keys, verify from another device, or log out. The picker already handles all
  // these options. Post-login: only show when a verified peer exists (so we don't offer SAS to
  // users with no other device).
  const showChangeMethod = isLoginUnlock || devicesVerificationStatus === CrossSigningStatusEnum.VERIFIED;

  return (
    <div data-testid={testingConstants.unlockWithSecurityCodeModal}>
      <SecurityCodeModalBase
        isVisible
        showCloseButton={false}
        onClose={() => undefined}
        icon={LockPin}
        title={t('verification.unlock_with_security_code_title')}
        footer={
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center">
            {showChangeMethod ? (
              <div className="flex w-full min-w-0 justify-center sm:max-w-[calc(100%-19rem)] sm:justify-start">
                <Button
                  variant="text"
                  type="button"
                  onClick={handleChangeMethod}
                  disabled={isSubmitting}
                  className="h-auto w-fit max-w-full min-w-0 !justify-center whitespace-normal text-center text-xs sm:!justify-start sm:text-left [&_.truncate]:whitespace-normal [&_.truncate]:overflow-visible"
                >
                  {t('verification.use_different_method')}
                </Button>
              </div>
            ) : null}
            <div className="flex w-full shrink-0 flex-col gap-4 sm:ml-auto sm:w-auto sm:flex-row sm:items-center">
              <Button
                variant="primary"
                size="md"
                className="h-12 w-full min-w-0 rounded-2xl px-6 sm:w-fit sm:min-w-[157px]"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting || securityCode.length !== SECURITY_CODE_LENGTH}
                data-testid={testingConstants.unlockWithSecurityCodeConfirmBtn}
              >
                {t('verification.unlock')}
              </Button>
            </div>
          </div>
        }
      >
        <p className="text-center text-gray-70">{t('verification.unlock_with_security_code_description')}</p>
        <div data-testid={testingConstants.unlockWithSecurityCodeInput}>
          <SecurityCodeInput
            ref={passcodeRef}
            value={securityCode}
            onChange={handleChange}
            onSubmit={handleSubmit}
            error={displayError}
            helperText={t('verification.unlock_with_security_code_helper')}
            autoFocus
            disabled={isSubmitting}
            isShaking={isShaking}
            onShakeEnd={() => setIsShaking(false)}
          />
        </div>
      </SecurityCodeModalBase>
    </div>
  );
};

/** Post-login path — matrix client must be initialized in React context. */
const PostLoginUnlockWithSecurityCodeModal = (props: UnlockWithSecurityCodeModalProps) => {
  const { unlockWithSecurityCode, isUnlocking, errorKey, clearError } = useSecretStorageSecurityCode();

  return (
    <UnlockWithSecurityCodeModalBody
      {...props}
      isLoginUnlock={false}
      isSubmitting={isUnlocking}
      activeErrorKey={errorKey}
      clearActiveError={clearError}
      submitSecurityCode={unlockWithSecurityCode}
    />
  );
};

/** Login-unlock path — client lives on matrixClientRef while startMatrixClient waits. */
const LoginUnlockWithSecurityCodeModal = (props: UnlockWithSecurityCodeModalProps) => {
  const { errorKey: loginUnlockErrorKey, isSubmitting: isLoginUnlockSubmitting } = useLoginSecretStorageUnlock();
  const submitCode = props.onSubmitSecurityCode ?? submitLoginSecretStorageUnlock;

  return (
    <UnlockWithSecurityCodeModalBody
      {...props}
      isLoginUnlock
      isSubmitting={isLoginUnlockSubmitting}
      activeErrorKey={loginUnlockErrorKey}
      clearActiveError={() => undefined}
      submitSecurityCode={submitCode}
    />
  );
};

const UnlockWithSecurityCodeModal = create<UnlockWithSecurityCodeModalProps>((props) => {
  const isLoginUnlock = props.isLoginUnlock ?? isLoginSecretStorageUnlockActive();

  if (isLoginUnlock) {
    return <LoginUnlockWithSecurityCodeModal {...props} />;
  }

  return <PostLoginUnlockWithSecurityCodeModal {...props} />;
});

export default UnlockWithSecurityCodeModal;
